import React, { useState } from 'react';
import DashboardLayout from '@/layouts/DashboardLayout';
import { CalendarView } from '@/components/calendar/CalendarView';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';

interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
}

const Calendar: React.FC = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedService, setSelectedService] = useState<string>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>();

  // Fetch services
  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('user_id', user?.id)
        .eq('is_active', true);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Generate time slots based on availability
  const timeSlots: TimeSlot[] = [
    { start: '09:00', end: '09:30', available: true },
    { start: '09:30', end: '10:00', available: true },
    { start: '10:00', end: '10:30', available: true },
    { start: '10:30', end: '11:00', available: true },
    { start: '11:00', end: '11:30', available: true },
    { start: '11:30', end: '12:00', available: true },
    { start: '13:00', end: '13:30', available: true },
    { start: '13:30', end: '14:00', available: true },
    { start: '14:00', end: '14:30', available: true },
    { start: '14:30', end: '15:00', available: true },
    { start: '15:00', end: '15:30', available: true },
    { start: '15:30', end: '16:00', available: true },
    { start: '16:00', end: '16:30', available: true },
    { start: '16:30', end: '17:00', available: true },
  ];

  return (
    <DashboardLayout>
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <CalendarView
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
            />
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Book Appointment</CardTitle>
                <CardDescription>
                  Select a service and time slot
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Service</label>
                  <Select
                    value={selectedService}
                    onValueChange={setSelectedService}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services?.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} ({service.duration}min)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedDate && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Available Times for {format(selectedDate, 'MMMM d, yyyy')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {timeSlots.map((slot) => (
                        <Button
                          key={slot.start}
                          variant={selectedTimeSlot === slot.start ? 'default' : 'outline'}
                          className="w-full"
                          disabled={!slot.available}
                          onClick={() => setSelectedTimeSlot(slot.start)}
                        >
                          {slot.start}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedDate && selectedService && selectedTimeSlot && (
                  <Button className="w-full">
                    Confirm Booking
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Calendar; 